import { FC } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { FieldGroup, FieldWrapper, FormInput, ScheduleFields } from "../../../shared/ui";
import { FormSelect } from "../../../shared/ui/controls/FormSelect";
import { AttachmentsSection } from "../../../shared/ui/controls/AttachmentsSection/AttachmentsSection";
import Checkbox from "../../../shared/ui/controls/Checkbox/Checkbox";
import { HandwerkerSearchSelect } from "../../../entities/user";
import {
  appointmentStatusOptions,
  createAppointmentStatusOptions,
} from "../../../entities/appointment";
import {
  hasActuals,
  type AppointmentFormValues,
} from "../model/appointmentForm.schema";

/**
 * Fields only — no queries, no mutations. Everything it needs arrives as props from
 * `UpsertAppointmentModal`, which owns the data and the submit.
 *
 * Three sections, the same three the Aufgabe form has: *Termin* (who, and in which state),
 * *Zeit* (the reservation — `ScheduleFields`, shared with `features/task-upsert` verbatim) and
 * *Details*. Ordered by decision, not by data model: who and when comes first because that is
 * what the user opened the dialog to choose; description and attachments are optional context
 * and sit last.
 */

const SERIES_SCOPE_LABELS = { single: "Nur dieser Termin", series: "Dieser und alle folgenden" };

interface Props {
  control: Control<AppointmentFormValues>;
  mode: "create" | "edit";
  /**
   * True whenever the work order is already known — from the page, the URL or the appointment
   * being edited. The Auftrag then lives in the modal subtitle instead, because it identifies what
   * is being scheduled rather than being something this form decides.
   *
   * The field below is what is left when it is false: a create with no work order at all, which
   * only a hand-typed `/disposition?disposition-mode=schedule-appointment` produces. `workOrderId`
   * is required by the schema, so without the input that URL would be an unsubmittable dead end.
   */
  workOrderLocked: boolean;
  /** Edit mode: the appointment belongs to a series, so the scope toggle applies. */
  hasSeries: boolean;
  /**
   * Create mode: show the Status select (Geplant / Folgetermin geplant). Resolved by the modal,
   * which owns the Auftrag query — a Folgetermin needs an earlier Termin to follow, so an Auftrag
   * without one gets no choice to make.
   */
  followUpSelectable?: boolean;
  /** Current `plannedStart` — the anchor the recurrence preview is derived from. */
  plannedStart: string;
  /**
   * Why Ganztägig did nothing, when it did nothing. Resolved by the modal, which owns the query —
   * this component stays free of both queries and mutations.
   */
  fullDayHint?: string;
  files: File[];
  onFilesChange: (files: File[]) => void;
  existingAttachments: string[];
  onRemoveExistingAttachment: (index: number) => void;
}

export const AppointmentFormFields: FC<Props> = ({
  control,
  mode,
  workOrderLocked,
  hasSeries,
  followUpSelectable = false,
  plannedStart,
  fullDayHint,
  files,
  onFilesChange,
  existingAttachments,
  onRemoveExistingAttachment,
}) => {
  /**
   * Read from the form rather than from the loaded appointment: the status is editable, so the
   * Ist-Zeiten have to appear in the same render as the user picking "Abgeschlossen". The mapper
   * gates on the same value — see `hasActuals`.
   */
  const status = useWatch({ control, name: "status" });

  return (
    <div className="flex w-full flex-col gap-6">
      <FieldGroup title="Termin">
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-6">
          {!workOrderLocked && (
            <FieldWrapper label="Auftrag" required className="col-span-2 sm:col-span-3">
              <FormInput name="workOrderId" control={control} as="input" />
            </FieldWrapper>
          )}

          <FieldWrapper label="Handwerker" required className="col-span-2 sm:col-span-3">
            <Controller
              name="assignedTechnicianId"
              control={control}
              render={({ field, fieldState }) => (
                <HandwerkerSearchSelect
                  value={field.value}
                  onChange={(id) => field.onChange(id)}
                  error={fieldState.error?.message ?? null}
                  inputRef={field.ref}
                  placeholder="Handwerker suchen..."
                />
              )}
            />
          </FieldWrapper>

          {/*
            Handwerker | Status is the first row of both forms — whoever looks for the status finds
            it in the same place on a Termin and on an Aufgabe.

            Create offers two states, Geplant and Folgetermin geplant, and only when a Folgetermin
            makes sense (`followUpSelectable`: the Auftrag already has a Termin to follow). Edit
            offers all five, EXPIRED included. Picking a completed one reveals the Ist-Zeiten below
            and needs a Manager rolle — the API answers 403 otherwise.
          */}
          {(mode === "edit" || followUpSelectable) && (
            <FieldWrapper label="Status" className="col-span-2 sm:col-span-3">
              <FormSelect
                name="status"
                control={control}
                options={
                  mode === "edit"
                    ? appointmentStatusOptions
                    : createAppointmentStatusOptions
                }
              />
            </FieldWrapper>
          )}
        </div>
      </FieldGroup>

      <ScheduleFields
        control={control}
        mode={mode}
        showActuals={hasActuals(status)}
        startRequired
        workRequired
        hasSeries={hasSeries}
        plannedStart={plannedStart}
        seriesScopeLabels={SERIES_SCOPE_LABELS}
        startAddon={
          // Ganztägig fills the Schicht: the three durations are rewritten so the belegter Block
          // runs shiftStart–shiftEnd. They stay editable, and editing one clears the checkbox
          // again — see `useFullDaySync`. Termin only: an Aufgabe has no all-day form.
          <div className="mt-2">
            <Controller
              name="isFullDay"
              control={control}
              render={({ field }) => (
                <Checkbox checked={field.value} onChange={field.onChange} label="Ganztägig" />
              )}
            />
            {fullDayHint && <p className="mt-1 text-xs text-amber-600">{fullDayHint}</p>}
          </div>
        }
      />

      <FieldGroup title="Details">
        <FieldWrapper label="Beschreibung">
          <FormInput name="description" control={control} as="textarea" rows={3} />
        </FieldWrapper>

        <AttachmentsSection
          existingAttachments={existingAttachments}
          onRemoveExistingAttachment={onRemoveExistingAttachment}
          files={files}
          onFilesChange={onFilesChange}
        />
      </FieldGroup>
    </div>
  );
};
